import { useState, useEffect } from "react";
import axios from "axios";
import { auth } from "../../config/firebase";
import { getFirestore, doc, onSnapshot, getDoc, setDoc, collection, updateDoc, serverTimestamp } from "firebase/firestore";
import '../../styles/Minigames.css';
import { Hearts } from 'react-loader-spinner';

export default function MindmatchGame() {
    const currentUser = auth.currentUser;
    const [relationship, setRelationship] = useState(null);
    const [gameId, setGameId] = useState(null);
    const [partnerUid, setPartnerUid] = useState(null);
    const [mySecret, setMySecret] = useState("");
    const [currentUserGuess, setCurrentUserGuess] = useState("");
    const [gameState, setGameState] = useState({
        currentUser: { guesses: [], started: false, correctGuess: false, secret: "" },
        connectedUser: { guesses: [], started: false, correctGuess: false, secret: "" }
    });
    const [loading, setLoading] = useState(true);
    const [bothStarted, setBothStarted] = useState(false);
    const [secretSubmitted, setSecretSubmitted] = useState(false);
    const [bothSecretSubmitted, setBothSecretSubmitted] = useState(false);
    const [isMyTurn, setIsMyTurn] = useState(false);
    const [gameResult, setGameResult] = useState(null);
    const [guessHistory, setGuessHistory] = useState([]);
    const [partnerGuessHistory, setPartnerGuessHistory] = useState([]);
    const [firstPlayerUid, setFirstPlayerUid] = useState(null);
    const [autoResetting, setAutoResetting] = useState(false);
    const [resetCountdown, setResetCountdown] = useState(5);
    const [gameData, setGameData] = useState(null);
    const [showExitWarning, setShowExitWarning] = useState(false);
    const db = getFirestore();


    // Fetch relationship data
    useEffect(() => {
        const fetchRelationship = async () => {
            try {
                const response = await axios.get(`https://api.frazza.online/user/${currentUser.uid}/relationship`);
                if (response.data.success && response.data.data.inRelation) {
                    setRelationship(response.data.data);
                    const partnerId = String(response.data.data.inRelationWith);
                    setPartnerUid(partnerId);

                    // Create game ID
                    const ids = [currentUser.uid, partnerId].sort();
                    const existingGameId = ids.join('_');
                    setGameId(existingGameId);
                    setLoading(false);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Failed to fetch relationship status:", error);
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchRelationship();
        }
    }, [currentUser]);

    useEffect(() => {
        if (!gameId || !currentUser?.uid || !partnerUid) return;

        const unsubscribeGame = onSnapshot(
            doc(db, "MindmatchGame", gameId),
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    setGameData(data);

                    if (data.firstPlayer) {
                        setFirstPlayerUid(data.firstPlayer);
                    }

                    if (data.autoResetting !== undefined) {
                        setAutoResetting(data.autoResetting);

                        // If auto-resetting was cancelled by another player
                        if (data.autoResetting === false && data.resetCancelledBy && data.resetCancelledBy !== currentUser.uid) {
                            // Reset local state
                            setMySecret("");
                            setCurrentUserGuess("");
                            setSecretSubmitted(false);
                            setBothSecretSubmitted(false);
                            setGameResult(null);
                            setFirstPlayerUid(null);
                            setGuessHistory([]);
                            setPartnerGuessHistory([]);
                        }

                        // Update countdown if auto-resetting is active
                        if (data.autoResetting && data.resetCountdown !== undefined) {
                            setResetCountdown(data.resetCountdown);
                        }
                    }
                }
            },
            (error) => console.error("Error in game listener:", error)
        );

        const unsubscribeCurrentUser = onSnapshot(
            doc(db, `MindmatchGame/${gameId}/${currentUser.uid}/gameData`),
            (docSnapshot) => {
                console.log("Current user snapshot received:", docSnapshot.exists(), docSnapshot.data());

                if (docSnapshot.exists()) {
                    const currentUserData = docSnapshot.data();

                    setGameState(prev => ({
                        ...prev,
                        currentUser: currentUserData
                    }));

                    if (currentUserData.secret) {
                        setSecretSubmitted(true);
                    }

                    setGuessHistory(currentUserData.guesses || []);

                    if (currentUserData.correctGuess) {
                        setGameResult('win');
                        // Reset turn state to prevent the "Opponent's Turn" state
                        setIsMyTurn(false);
                        setBothSecretSubmitted(false);
                    }
                }
            },
            (error) => console.error("Error in current user listener:", error)
        );

        // Listen to partner's data
        const unsubscribePartner = onSnapshot(
            doc(db, `MindmatchGame/${gameId}/${partnerUid}/gameData`),
            (docSnapshot) => {
                console.log("Partner snapshot received:", docSnapshot.exists(), docSnapshot.data());

                if (docSnapshot.exists()) {
                    const partnerData = docSnapshot.data();

                    // Check if partner's started state changed from true to false
                    if (gameState.connectedUser.started && !partnerData.started) {
                        setSecretSubmitted(false);
                        setBothSecretSubmitted(false);
                        setMySecret("");
                    }

                    // Check if partner exited the game
                    if (partnerData.exitedBy) {
                        // Reset game states
                        setSecretSubmitted(false);
                        setBothSecretSubmitted(false);
                        setMySecret("");
                        setCurrentUserGuess("");
                        setGameResult(null);
                        setFirstPlayerUid(null);
                        setGuessHistory([]);
                        setPartnerGuessHistory([]);
                        setIsMyTurn(false);
                    }

                    setGameState(prev => ({
                        ...prev,
                        connectedUser: partnerData
                    }));

                    setPartnerGuessHistory(partnerData.guesses || []);

                    if (partnerData.correctGuess) {
                        setGameResult('lose');
                        // Reset turn state to prevent the "Opponent's Turn" state
                        setIsMyTurn(false);
                        setBothSecretSubmitted(false);
                    }
                }
            },
            (error) => console.error("Error in partner listener:", error)
        );

        return () => {
            unsubscribeCurrentUser();
            unsubscribePartner();
            unsubscribeGame();
        };
    }, [gameId, currentUser?.uid, partnerUid, db]);

    useEffect(() => {
        if (gameState.currentUser.started && gameState.connectedUser.started) {
            setBothStarted(true);
        } else if (!gameState.currentUser.started || !gameState.connectedUser.started) {
            setBothStarted(false);
        }
    }, [gameState.currentUser.started, gameState.connectedUser.started]);

    // Check for game results
    useEffect(() => {
        // Check for win condition
        if (gameState.currentUser.correctGuess) {
            setGameResult('win');
            // Reset turn state to prevent the "Opponent's Turn" state
            setIsMyTurn(false);
            setBothSecretSubmitted(false);
        }

        if (gameState.connectedUser.correctGuess && !gameState.currentUser.correctGuess) {
            setGameResult('lose');
            // Reset turn state to prevent the "Opponent's Turn" state
            setIsMyTurn(false);
            setBothSecretSubmitted(false);
            // Auto-reset after delay
        }

        // Check for tie condition
        if (gameState.currentUser.correctGuess && gameState.connectedUser.correctGuess) {
            setGameResult('tie');
            // Reset turn state to prevent the "Opponent's Turn" state
            setIsMyTurn(false);
            setBothSecretSubmitted(false);
            // Auto-reset after delay
        }
    }, [gameState.currentUser.correctGuess, gameState.connectedUser.correctGuess]);

    // Process game state changes - secrets submitted
    useEffect(() => {
        // Check if both have submitted secrets
        if (gameState.currentUser.secret && gameState.connectedUser.secret) {
            console.log("Both players have submitted secrets!");
            setBothSecretSubmitted(true);

            // First player selection
            if (gameData && !gameData.firstPlayerSet) {
                // Only one client should set the first player
                // Use the alphabetically first UID to decide which client sets it
                const shouldSetFirstPlayer = currentUser.uid < partnerUid;

                if (shouldSetFirstPlayer) {
                    console.log("This client is setting first player");
                    const randomFirstPlayer = Math.random() >= 0.5 ? currentUser.uid : partnerUid;

                    // Update Firestore with first player selection
                    const gameDocRef = doc(db, "MindmatchGame", gameId);
                    updateDoc(gameDocRef, {
                        firstPlayer: randomFirstPlayer,
                        firstPlayerSet: true,
                        timestamp: serverTimestamp()
                    })
                        .then(() => console.log("First player set to:", randomFirstPlayer))
                        .catch(err => console.error("Error setting first player:", err));
                } else {
                    console.log("Waiting for other client to set first player");
                }
            }
        } else if (bothSecretSubmitted) {
            // If we were in a state where both secrets were submitted, but now one is missing,
            // it means one player has reset or exited the game
            console.log("One player has reset or exited the game, resetting bothSecretSubmitted");
            setBothSecretSubmitted(false);
            setIsMyTurn(false);
        }
    }, [gameState.currentUser.secret, gameState.connectedUser.secret, gameData, gameId, currentUser?.uid, partnerUid, db, bothSecretSubmitted]);

    // Determine turn based on game state
    useEffect(() => {
        if (bothSecretSubmitted && firstPlayerUid) {
            const myGuesses = gameState.currentUser.guesses?.length || 0;
            const partnerGuesses = gameState.connectedUser.guesses?.length || 0;

            // Don't update turns if game is over
            if (gameResult) {
                return;
            }

            if (myGuesses === 0 && partnerGuesses === 0) {
                // First turn
                const myTurn = firstPlayerUid === currentUser.uid;
                setIsMyTurn(myTurn);
                console.log("First turn set to:", myTurn);
            } else {
                // Alternate turns
                let myTurn;
                if (firstPlayerUid === currentUser.uid) {
                    // If I'm the first player, it's my turn when I've made the same or fewer guesses
                    myTurn = myGuesses <= partnerGuesses;
                } else {
                    // If partner is the first player, it's my turn when I've made fewer guesses
                    // Special case: If partner has made exactly 1 guess and I've made 0, it's my turn
                    myTurn = myGuesses < partnerGuesses || (myGuesses === 0 && partnerGuesses === 1);
                }
                setIsMyTurn(myTurn);
                console.log("Turn updated:", myTurn, "My guesses:", myGuesses, "Partner guesses:", partnerGuesses, "Special case triggered:", firstPlayerUid !== currentUser.uid && myGuesses === 0 && partnerGuesses === 1);
            }
        } else {
            // If not both secrets submitted or no first player, it's not anyone's turn
            setIsMyTurn(false);
        }
    }, [
        bothSecretSubmitted, 
        firstPlayerUid, 
        gameState.currentUser.guesses, 
        gameState.connectedUser.guesses, 
        currentUser?.uid, 
        gameResult
    ]);

    // Reset countdown timer
    useEffect(() => {
        let timer;
        if (gameResult && autoResetting) {
            // Only the player who initiated the auto-reset should update the countdown
            const shouldUpdateCountdown = gameData?.resetInitiator === currentUser.uid;

            if (shouldUpdateCountdown) {
                setResetCountdown(5);
                timer = setInterval(async () => {
                    setResetCountdown(prev => {
                        const newCount = prev <= 1 ? 0 : prev - 1;

                        // Update Firestore with new countdown value
                        if (gameId) {
                            const gameDocRef = doc(db, "MindmatchGame", gameId);
                            updateDoc(gameDocRef, {
                                resetCountdown: newCount
                            }).catch(err => console.error("Error updating countdown:", err));

                            if (newCount === 0) {
                                clearInterval(timer);
                            }
                        }

                        return newCount;
                    });
                }, 1000);
            }
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [gameResult, autoResetting, gameId, currentUser?.uid, gameData?.resetInitiator]);

    const handleExitGame = async () => {
        try {
            if (!gameId) return;

            // Get user display name
            const displayName = currentUser.displayName || "Your opponent";

            // First, update both players' game data to indicate exit
            // This ensures the other player detects the exit before document deletion
            const currentUserDocRef = doc(db, `MindmatchGame/${gameId}/${currentUser.uid}/gameData`);
            await updateDoc(currentUserDocRef, {
                exitedBy: currentUser.uid,
                exitedAt: serverTimestamp(),
                started: false
            });

            const partnerDocRef = doc(db, `MindmatchGame/${gameId}/${partnerUid}/gameData`);
            await updateDoc(partnerDocRef, {
                exitedBy: currentUser.uid,
                exitedAt: serverTimestamp(),
                started: false
            });

            // Use the server's reset endpoint to properly reset both players' states
            await axios.post(
                `https://api.frazza.online/minigames/mindmatch/${gameId}/${currentUser.uid}/reset`,
                {
                    exitGame: true,
                    userName: displayName
                }
            );

            // Update game document to notify other player and cancel any auto-reset
            const gameDocRef = doc(db, "MindmatchGame", gameId);
            await updateDoc(gameDocRef, {
                endedBy: currentUser.uid,
                enderName: displayName,
                endedAt: serverTimestamp(),
                autoResetting: false, // Cancel any auto-reset
                resetCountdown: 0,
                resetCancelledBy: currentUser.uid,
                resetCancelledAt: serverTimestamp()
            });

            // Delete the game document completely
            await axios.delete(
                `https://api.frazza.online/minigames/mindmatch/${gameId}/delete`
            );

            // No need to update player documents as they've been deleted

            // Reset all local state
            setMySecret("");
            setCurrentUserGuess("");
            setSecretSubmitted(false);
            setBothSecretSubmitted(false);
            setGameResult(null);
            setFirstPlayerUid(null);
            setGuessHistory([]);
            setPartnerGuessHistory([]);
            setGameData(null);
            setBothStarted(false);
            setShowExitWarning(false);
            setIsMyTurn(false); // Reset turn state
            setAutoResetting(false); // Reset auto-resetting state
            setGameState({
                currentUser: { guesses: [], started: false, correctGuess: false, secret: "" },
                connectedUser: { guesses: [], started: false, correctGuess: false, secret: "" }
            });

            console.log("Successfully exited game");
        } catch (error) {
            console.error("Error exiting game:", error);
            alert("Failed to exit game. Please try again.");
            setShowExitWarning(false);
        }
    };

    const handleStartGame = async () => {
        try {
            console.log("Starting game...");


            // Initialize the game
            const initResponse = await axios.post("https://api.frazza.online/minigames/mindmatch/initialize", {
                currentUserId: currentUser.uid,
                connectedUserId: partnerUid
            });

            if (initResponse.data.success) {
                // Start the game for current user
                await axios.post(
                    `https://api.frazza.online/minigames/mindmatch/${gameId}/${currentUser.uid}/start`
                );

                // Update local state immediately for better UX
                setGameState(prev => ({
                    ...prev,
                    currentUser: { ...prev.currentUser, started: true }
                }));
            }
        } catch (error) {
            console.error("Error starting game:", error);
        }
    };

    const handleSecretSubmit = async () => {
        if (!mySecret || mySecret.length !== 4 || !/^\d{4}$/.test(mySecret)) {
            alert("Please enter exactly 4 digits");
            return;
        }

        try {
            await axios.post(
                `https://api.frazza.online/minigames/mindmatch/${gameId}/${currentUser.uid}/number`,
                { secret: mySecret }
            );

            // Update local state immediately
            setGameState(prev => ({
                ...prev,
                currentUser: { ...prev.currentUser, secret: mySecret }
            }));
            setSecretSubmitted(true);
        } catch (error) {
            console.error("Error submitting secret:", error);
        }
    };

    const handleGuessSubmit = async () => {
        if (!currentUserGuess || currentUserGuess.length !== 4 || !/^\d{4}$/.test(currentUserGuess)) {
            alert("Please enter exactly 4 digits");
            return;
        }

        try {
            const statusResponse = await axios.get(
                `https://api.frazza.online/minigames/mindmatch/${gameId}/${currentUser.uid}/status?partnerId=${partnerUid}`
            );

            if (statusResponse.data.success) {
                const partnerSecret = String(statusResponse.data.partnerSecret);
                const guessAsString = String(currentUserGuess);

                console.log("Your guess:", guessAsString);
                console.log("Partner secret:", partnerSecret);

                let exactMatches = 0;
                for (let i = 0; i < 4; i++) {
                    const isMatch = guessAsString[i] === partnerSecret[i];
                    console.log(`Position ${i}: ${guessAsString[i]} vs ${partnerSecret[i]} = ${isMatch ? "MATCH" : "NO MATCH"}`);
                    if (isMatch) {
                        exactMatches++;
                    }
                }

                console.log(`Total position matches: ${exactMatches}`);

                const response = await axios.post(
                    `https://api.frazza.online/minigames/mindmatch/${gameId}/${currentUser.uid}/guess`,
                    {
                        guess: guessAsString,
                        opponentSecret: partnerSecret,
                        matchCount: exactMatches,
                        isClientCalc: true
                    }
                );

                console.log("Server response:", response.data);

                // Let the server handle updating Firestore
                // We'll just update the local state for immediate feedback
                const newGuess = {
                    guess: guessAsString,
                    exactMatches: exactMatches,
                    matchCount: exactMatches
                };

                // Update local state with the new guess for UI feedback
                setGuessHistory(prev => [...prev, newGuess]);

                setCurrentUserGuess("");
                setIsMyTurn(false);
            }
        } catch (error) {
            console.error("Error submitting guess:", error);
        }
    };

    const handleResetGame = async () => {
        try {
            console.log("Manual game reset initiated");

            // First, update Firestore to cancel any auto-reset and mark manual reset in progress
            const gameDocRef = doc(db, "MindmatchGame", gameId);
            await updateDoc(gameDocRef, {
                autoResetting: false,
                resetCountdown: 0,
                resetCancelledBy: currentUser.uid,
                resetCancelledAt: serverTimestamp(),
                manualResetInProgress: true,
                manualResetInitiator: currentUser.uid,
                manualResetStartTime: serverTimestamp()
            });

            console.log("Firestore updated for manual reset");

            // Get user display name for better tracking
            const displayName = currentUser.displayName || "Player";

            // Delete the existing game document
            await axios.delete(
                `https://api.frazza.online/minigames/mindmatch/${gameId}/delete`
            );

            console.log("Existing game document deleted");

            // Reset local state
            setMySecret("");
            setCurrentUserGuess("");
            setSecretSubmitted(false);
            setBothSecretSubmitted(false);
            setGameResult(null);
            setFirstPlayerUid(null);
            setGuessHistory([]);
            setPartnerGuessHistory([]);
            setAutoResetting(false); // Make sure to reset the auto-resetting state

            // Initialize a new game with both users started=true
            const initResponse = await axios.post("https://api.frazza.online/minigames/mindmatch/initialize", {
                currentUserId: currentUser.uid,
                connectedUserId: partnerUid
            });

            if (initResponse.data.success) {
                // Start the game for current user
                await axios.post(
                    `https://api.frazza.online/minigames/mindmatch/${gameId}/${currentUser.uid}/start`
                );

                // Start the game for partner user
                await axios.post(
                    `https://api.frazza.online/minigames/mindmatch/${gameId}/${partnerUid}/start`
                );

                // Update local state immediately for better UX
                setGameState(prev => ({
                    ...prev,
                    currentUser: { ...prev.currentUser, started: true },
                    connectedUser: { ...prev.connectedUser, started: true }
                }));

                setBothStarted(true);
            }

            console.log("New game initialized with both players started");

            console.log("Manual reset completed successfully");
        } catch (error) {
            console.error("Error resetting game:", error);

            // Try to update Firestore to indicate reset failed
            try {
                const gameDocRef = doc(db, "MindmatchGame", gameId);
                await updateDoc(gameDocRef, {
                    manualResetInProgress: false,
                    manualResetFailed: true,
                    manualResetFailedAt: serverTimestamp(),
                    manualResetFailedBy: currentUser.uid,
                    manualResetError: error.message
                });
            } catch (updateError) {
                console.error("Error updating manual reset failure state:", updateError);
            }

            // Alert the user
            alert("Failed to reset the game. Please try again.");
        }
    };

    // Warning Dialog Component
    const ExitWarningDialog = ({ onConfirm, onCancel }) => (
        <div className="mm-dialog-overlay">
            <div className="mm-dialog">
                <h3>Exit Game</h3>
                <p>Are you sure you want to exit? All progress and data will be reset.</p>
                <div className="mm-dialog-buttons">
                    <button onClick={onCancel} className="mm-button secondary">Cancel</button>
                    <button onClick={onConfirm} className="mm-button exit">Exit Game</button>
                </div>
            </div>
        </div>
    );


    // Digit component for display
    const Digit = ({ value }) => (
        <div className="mm-digit">{value}</div>
    );

    // Loading state
    if (loading) {
        return (
            <div className="mm-container">
                <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
            </div>
        );
    }

    // No relationship state
    if (!relationship || !relationship.inRelation) {
        return (
            <div className="mm-container">
                <h1 className="mm-title">Mindmatch 4D</h1>
                <p className="mm-message">You need to be in a relationship to play this game.</p>
            </div>
        );
    }

    return (
        <div className="mm-container">
            <h1 className="mm-title">Mindmatch 4D</h1>

            {/* Game Status Banner */}
            <div className="mm-status">
                {gameResult ? (
                    <div className={`mm-banner ${gameResult}`}>
                        {gameResult === 'win' ? '🎉 You Won!' :
                            gameResult === 'tie' ? '🤝 It\'s a Tie!' :
                                '😔 You Lost'}
                        {autoResetting && <div className="mm-countdown">New game in {resetCountdown}...</div>}
                    </div>
                ) : bothSecretSubmitted ? (
                    <div className={`mm-turn ${isMyTurn ? 'active' : ''}`}>
                        {isMyTurn ? 'Your Turn' : 'Opponent\'s Turn'}
                    </div>
                ) : secretSubmitted ? (
                    <div className="mm-waiting">Waiting for opponent to select number...</div>
                ) : bothStarted ? (
                    <div className="mm-prompt">Enter your secret 4-digit number</div>
                ) : gameState.currentUser.started ? (
                    <div className="mm-waiting">Waiting for opponent to join...</div>
                ) : (
                    <div className="mm-prompt">Ready to play?</div>
                )}
            </div>

            {/* Game Content */}
            <div className="mm-content">
                {gameResult ? (
                    <div className="mm-result">
                        <div className="mm-secret">
                            <p>Your opponent's secret:</p>
                            <div className="mm-number">
                                {gameState.connectedUser.secret?.split('').map((digit, i) => (
                                    <Digit key={i} value={digit} />
                                ))}
                            </div>
                        </div>
                        <button className="mm-button primary" onClick={handleResetGame}>
                            Play Again
                        </button>
                    </div>
                ) : (
                    !gameState.currentUser.started ? (
                        <button className="mm-button primary" onClick={handleStartGame}>
                            Start Game
                        </button>
                    ) : (
                        bothStarted && !secretSubmitted ? (
                            <div className="mm-secret-input">
                                <div className="mm-numpad">
                                    <div className="mm-display">
                                        {Array(4).fill(0).map((_, i) => (
                                            <Digit key={i} value={mySecret[i] || ''} />
                                        ))}
                                    </div>
                                    <div className="mm-keys">
                                        {[1,2,3,4,5,6,7,8,9,0].map(num => (
                                            <button
                                                key={num}
                                                className="mm-key"
                                                onClick={() => mySecret.length < 4 && setMySecret(prev => prev + num)}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                        <button
                                            className="mm-key clear"
                                            onClick={() => setMySecret('')}
                                        >
                                            Clear
                                        </button>
                                        <button
                                            className="mm-key backspace"
                                            onClick={() => setMySecret(prev => prev.slice(0, -1))}
                                        >
                                            ⌫
                                        </button>
                                    </div>
                                    <button
                                        className="mm-button primary"
                                        onClick={handleSecretSubmit}
                                        disabled={mySecret.length !== 4}
                                    >
                                        Confirm Secret
                                    </button>
                                </div>
                            </div>
                        ) : (
                            bothSecretSubmitted && (
                                <div className="mm-gameplay">
                                    {isMyTurn && (
                                        <div className="mm-guess-input">
                                            <div className="mm-numpad">
                                                <div className="mm-display">
                                                    {Array(4).fill(0).map((_, i) => (
                                                        <Digit key={i} value={currentUserGuess[i] || ''} />
                                                    ))}
                                                </div>
                                                <div className="mm-keys">
                                                    {[1,2,3,4,5,6,7,8,9,0].map(num => (
                                                        <button
                                                            key={num}
                                                            className="mm-key"
                                                            onClick={() => currentUserGuess.length < 4 && setCurrentUserGuess(prev => prev + num)}
                                                        >
                                                            {num}
                                                        </button>
                                                    ))}
                                                    <button
                                                        className="mm-key clear"
                                                        onClick={() => setCurrentUserGuess('')}
                                                    >
                                                        Clear
                                                    </button>
                                                    <button
                                                        className="mm-key backspace"
                                                        onClick={() => setCurrentUserGuess(prev => prev.slice(0, -1))}
                                                    >
                                                        ⌫
                                                    </button>
                                                </div>
                                                <button
                                                    className="mm-button primary"
                                                    onClick={handleGuessSubmit}
                                                    disabled={currentUserGuess.length !== 4}
                                                >
                                                    Make Guess
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mm-history">
                                        <div className="mm-history-column">
                                            <h2>Your Guesses</h2>
                                            <div className="mm-guesses">
                                                {guessHistory.length === 0 ? (
                                                    <p className="mm-no-guesses">No guesses yet</p>
                                                ) : (
                                                    // Show newest first
                                                    guessHistory.slice().reverse().map((guess, idx) => (
                                                        <div key={`${guess.guess}-${idx}`} className="mm-guess">
                                                            <div className="mm-guess-number">
                                                                {[...guess.guess].map((digit, i) => (
                                                                    <Digit key={i} value={digit} />
                                                                ))}
                                                            </div>
                                                            <div className="mm-match">
                                                                {guess.exactMatches || guess.matchCount} in correct position
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                        <div className="mm-history-column">
                                            <h2>Opponent's Guesses</h2>
                                            <div className="mm-guesses">
                                                {partnerGuessHistory.length === 0 ? (
                                                    <p className="mm-no-guesses">No guesses yet</p>
                                                ) : (
                                                    // Show newest first
                                                    partnerGuessHistory.slice().reverse().map((guess, idx) => (
                                                        <div key={`${guess.guess}-${idx}`} className="mm-guess">
                                                            <div className="mm-guess-number">
                                                                {[...guess.guess].map((digit, i) => (
                                                                    <Digit key={i} value={digit} />
                                                                ))}
                                                            </div>
                                                            <div className="mm-match">
                                                                {guess.exactMatches || guess.matchCount} in correct position
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        )
                    )
                )}
            </div>

            {gameState.currentUser.started && (
                <button
                    className="mm-button exit"
                    onClick={() => setShowExitWarning(true)}
                >
                    Exit Game
                </button>
            )}

            {showExitWarning && (
                <ExitWarningDialog
                    onConfirm={handleExitGame}
                    onCancel={() => setShowExitWarning(false)}
                />
            )}
        </div>
    );
}
